<template>
  <main class="main">
    <div class="container">
      <LazyUiBreadCrumbsV2
        :items="[
          {
            title: t('cs2_markets') || '',
            link: localePath('/markets'),
          },
          {
            title: data?.data?.market?.market_full_name || '',
            link: localePath(`/markets/${route.params.name}`),
          },
        ]"
      />
      <PagesMarket :market="(data?.data?.market || {}) as MarketFullInfo" />
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { MarketFullInfo } from '~/graphql/generated/schema-types';
import { useAlternateLinks } from '~/composables/alternate-links';
import { Period } from '~/graphql/generated/schema-types';
import { getFullMarketInfo } from '~/utils/api';

// definePageMeta({
//   middleware: ['admin']
// })

const route = useRoute();
const { locale, t } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig();
const imagesUrl = config?.public?.imagesUrl || '';

const period = ref<Period>(Period.OneMonth);

const { data, error } = await useAsyncData('getMarketsPage', () =>
  getFullMarketInfo(route.params.name as string, period.value, locale.value));

if (error.value || data.value?.errors?.length) {
  console.error('Error fetching markets', error.value?.message || data.value?.errors?.[0]?.message);
  throw createError({
    status: 404,
    statusText:
      `Error fetching markets page error: ${
        error.value?.message || data.value?.errors?.[0]?.message}`,
  });
}

const title = computed(() =>
  t('heads.market.title', { name: data.value?.data?.market?.market_full_name }),
);
const description = computed(() =>
  t('heads.market.description', { name: data.value?.data?.market?.market_full_name }),
);

const mainIcon = computed(() =>
  getImageFromProxy(`${imagesUrl}markets/banners/${data.value?.data?.market?.market_name}.svg`),
);

const { alternateLinks, canonical } = useAlternateLinks(`/markets/${route.params.name}`);

useHead({
  title: () => title.value,
  meta: [
    {
      name: 'keywords',
      content:
        `${data.value?.data?.market?.market_name
        } ,steam, counter strike global offensive, csgo, cs2, skins, items, markets`,
    },
    ...(!['en', 'ru'].includes(locale.value)
      ? [
          {
            name: 'robots',
            content: 'noindex, nofollow',
          },
        ]
      : []),
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => title.value,
  ogTitle: () => title.value,
  description: () => description.value,
  ogDescription: () => description.value,
  ogImage: () => mainIcon.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => title.value,
  twitterDescription: () => description.value,
  twitterImage: () => mainIcon.value,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: t('main'), item: '/' },
      {
        name: t('cs2_markets'),
        item: localePath('/markets'),
      },
      {
        name: title,
        item: localePath(`/markets${route.params.name}`),
      },
    ],
  }),

  defineWebPage({
    '@type': 'WebPage',
    'url': localePath(`/markets${route.params.name}`),
    'name': title,
    'description': description,
    'image': mainIcon,
    'publisher': {
      '@type': 'Organization',
      'name': 'CS Market Cap',
      'logo': {
        '@type': 'ImageObject',
        'url': mainIcon,
      },
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': localePath('/markets'),
      'name': data.value?.data?.market?.market_full_name,
      'mainEntity': {
        '@type': 'Organization',
        'name': data.value?.data?.market?.market_full_name,
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': data.value?.data?.market?.rating || 0,
          'reviewCount': data.value?.data?.market?.votes || 0,
        },
        'additionalProperty': [
          {
            '@type': 'PropertyValue',
            'name': t('unique_items'),
            'value': data.value?.data?.market?.supply_statistic?.items || 0,
          },
          {
            '@type': 'PropertyValue',
            'name': t('average_discount'),
            'value': `${data.value?.data?.market?.supply_statistic?.avg_discount}%`,
          },
          {
            '@type': 'PropertyValue',
            'name': t('monthly_visits'),
            'value': data.value?.data?.market?.monthly_visit || 'N/A',
          },
        ],
      },
    },
  }),
]);
</script>

<style lang="scss" scoped>
.main {
  margin-top: 20px;
  margin-bottom: 80px;
  @media screen and (max-width: 768px) {
    margin-bottom: 40px;
  }
}
</style>
